import React, { FC, useState } from "react";

import { FormStateType } from "../Helpers/formTypes";
import { SummaryPart } from "./SummaryPart";
import { useLabels } from "../Hooks/useLabels";
import { CancellationMessage } from "../CancellationMessage/CancellationMessage";
import { RootDataType } from "../Helpers/interfaces";
import { PricingSummaryModal } from "../FormElements/Modal/PricingSummaryModal/PricingSummaryModal";
import {
	calculateTotalPrice,
	getBaseEndPrice,
} from "../Helpers/totalPriceCalculator";
import { PriceDetailsButton } from "../PriceDetailsButton/PriceDetailsButton";
import { Divider } from "../Divider/Divider";
import {
	CAMPAIGN_PRICE,
	CENTER_NAME,
	DATE_AND_TIME,
	DOCTOR_NAME,
	PRICE,
	SERVICE_NAME,
	SPECIALIZATION_NAME,
} from "./labels";
import { displayPrice, formatDate } from "./helpers";

type SummaryProps = {
	step: number;
	state: FormStateType;
	rootData: RootDataType;
};

export const Summary: FC<SummaryProps> = ({ step, state, rootData }) => {
	const { bookingLabels } = useLabels();
	const [showPriceSummaryModal, setShowPriceSummaryModal] = useState(false);

	const displayPriceInfo = !!state.pricingSummary.id;
	const displayPriceDetailsButton = state.service.disablePriceDetails == false;

	const displayAppointmentInfo =
		step >= 2 && !!state.appointment.serviceVariantId;

	const displayCancellationInfo = displayAppointmentInfo && !!state.appointment;

	const startDate = state.appointment.startTime;

	const title =
		bookingLabels?.stepsBookingLabels.secondStepLabels.summarySubHeading ??
		"Oppsummering";

	const isDoctorSelected = state.doctor.id && state.doctor.id != "-1";
	const doctor =
		isDoctorSelected && !state.appointment?.doctorId
			? state.doctor
			: rootData.doctors?.filter((d) => d.id == state.appointment.doctorId)[0];
	const center =
		state.location.id &&
		state.location.id != "-1" &&
		state.location.isLocation == false &&
		!state.appointment?.centerId
			? state.location
			: rootData.centers?.filter((c) => c.id == state.appointment.centerId)[0];

	const formattedAppointmentDate = startDate && formatDate(startDate);

	const serviceName = () => {
		if (state.appointment.serviceId) {
			if (state.appointment.isRelatedAppointment) {
				return rootData.services.find(
					(s) => s.id === state.appointment.serviceId
				)?.bookingName;
			}
			return rootData.services.find(
				(s) => s.id === state.appointment.serviceId
			)!.summaryName;
		}
		return state.service.summaryName;
	};

	const specializationName = state.specializationName;

	const totalPrice = displayPriceInfo
		? calculateTotalPrice(state.pricingSummary)
		: "";

	const baseEndPrice = getBaseEndPrice(state.pricingSummary);

	return (
		<div className="Summary">
			<div className="Summary__content">
				<h3 className="Summary__title">{title}</h3>
				<dl className="Summary__list">
					{!!specializationName && (
						<SummaryPart
							label={SPECIALIZATION_NAME}
							content={specializationName}
						/>
					)}
					<SummaryPart label={SERVICE_NAME} content={serviceName()} />
					{doctor && (
						<SummaryPart
							showJobTitle={state.service.showDoctorsJobTitle}
							label={DOCTOR_NAME}
							content={doctor.name}
							additionalContent={!isDoctorSelected ? doctor.jobTitle : ""}
						/>
					)}
					{displayAppointmentInfo && (
						<SummaryPart
							label={DATE_AND_TIME}
							content={formattedAppointmentDate}
						/>
					)}
					{center && <SummaryPart label={CENTER_NAME} content={center.name} />}

					{displayPriceInfo && (
						<SummaryPart
							label={
								!!state.pricingSummary.campaignPrice ? CAMPAIGN_PRICE : PRICE
							}
						>
							{!state.pricingSummary.listWithoutPrice && (
								<div className={"Summary__price"}>
									{displayPrice(totalPrice)}

									{!!state.pricingSummary.campaignPrice && (
										<span className="Summary__price__old">
											Før {baseEndPrice.toLocaleString("nb")},-
										</span>
									)}
								</div>
							)}
							{displayPriceDetailsButton && (
								<PriceDetailsButton
									service={state.service}
									bookingLabels={bookingLabels!}
									onClick={() => setShowPriceSummaryModal(true)}
								/>
							)}
						</SummaryPart>
					)}
				</dl>
				{displayCancellationInfo && (
					<CancellationMessage
						message={state.cancellationDeadline?.message || ""}
					/>
				)}
			</div>
			<PricingSummaryModal
				onClose={() => setShowPriceSummaryModal(false)}
				isOpen={showPriceSummaryModal}
				pricingSummary={state.pricingSummary}
			/>
		</div>
	);
};
